import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { api, useAction, useApi } from "../../api.ts";
import { fmtDateTime, fmtRelative } from "../../format.ts";
import { useLiveEvent } from "../../live.tsx";
import {
  Badge, Button, Card, EmptyState, ErrorText, Icon, Loading, LoadError, PageHeader, Select, Tabs, Textarea, cn, useToast,
} from "../../ui/index.tsx";
import { PRIORITY_TONE, TICKET_STATUS, useTick } from "./_common.tsx";
import type { Ticket, TicketPriority, TicketStatus, TicketSummary } from "../../../shared/types.ts";

type Filter = "all" | TicketStatus;
const STATUSES = Object.keys(TICKET_STATUS) as TicketStatus[];
const PRIORITIES: TicketPriority[] = ["low", "normal", "high"];

export default function Tickets() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const filter = (STATUSES.find((s) => s === params.get("status")) ?? "all") as Filter;
  const list = useApi<TicketSummary[]>("/admin/tickets");
  const ticket = useApi<Ticket>(id ? `/admin/tickets/${id}` : null);
  useTick();

  useLiveEvent(["ticket"], (m) => {
    void list.reload();
    if (m.type === "ticket" && id && m.ticket_id === Number(id)) void ticket.reload();
  });

  const all = list.data ?? [];
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: all.length, open: 0, in_progress: 0, resolved: 0, closed: 0 };
    for (const t of all) c[t.status]++;
    return c;
  }, [all]);
  const visible = filter === "all" ? all : all.filter((t) => t.status === filter);

  const setFilter = (f: Filter) => {
    const p = new URLSearchParams(params);
    f === "all" ? p.delete("status") : p.set("status", f);
    setParams(p, { replace: true });
  };

  return (
    <>
      <PageHeader title="Tickets" description="Support inbox" />
      <Card padded={false}>
        <div className="px-4 pt-2">
          <Tabs<Filter> value={filter} onChange={setFilter} tabs={[
            { id: "all", label: "All", count: counts.all },
            ...STATUSES.map((s) => ({ id: s as Filter, label: TICKET_STATUS[s].label, count: counts[s] })),
          ]} />
        </div>
        <div className="grid lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <div className={cn("max-h-[70vh] overflow-y-auto border-line lg:border-r", id && "hidden lg:block")}>
            {list.error && !list.data ? <LoadError error={list.error} retry={list.reload} /> : !list.data ? <Loading /> : visible.length === 0 ? (
              <EmptyState icon="ticket" title="No tickets" body={filter === "all" ? "Nothing in the inbox." : `No ${TICKET_STATUS[filter].label.toLowerCase()} tickets.`} />
            ) : (
              <ul className="divide-y divide-line">
                {visible.map((t) => {
                  const selected = String(t.id) === id;
                  const unread = t.status === "open";
                  return (
                    <li key={t.id}>
                      <button type="button" onClick={() => navigate({ pathname: `/admin/tickets/${t.id}`, search: location.search })}
                        className={cn("block w-full px-4 py-3 text-left transition-colors hover:bg-hover/60", selected && "bg-raised shadow-[inset_2px_0_0_var(--color-accent)]")}>
                        <div className="flex items-center gap-2">
                          {unread && <span className="size-1.5 shrink-0 rounded-full bg-accent" />}
                          <span className={cn("min-w-0 flex-1 truncate text-sm", unread ? "font-semibold" : "text-muted")}>{t.subject}</span>
                          <span className="shrink-0 text-[11px] text-faint">{fmtRelative(t.updated_at)}</span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted">
                          <span className="truncate">{t.username}</span>
                          <Badge tone={TICKET_STATUS[t.status].tone}>{TICKET_STATUS[t.status].label}</Badge>
                          {t.priority !== "normal" && <Badge tone={PRIORITY_TONE[t.priority]}>{t.priority}</Badge>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className={cn("min-h-[24rem]", !id && "hidden lg:block")}>
            {!id ? (
              <EmptyState icon="ticket" title="Select a ticket" body="Pick a conversation from the list." />
            ) : ticket.error && !ticket.data ? (
              <LoadError error={ticket.error} retry={ticket.reload} />
            ) : !ticket.data || String(ticket.data.id) !== id ? (
              <Loading />
            ) : (
              <Thread key={id} ticket={ticket.data} backTo={{ pathname: "/admin/tickets", search: location.search }}
                onChange={(t) => { ticket.setData(t); void list.reload(); }} />
            )}
          </div>
        </div>
      </Card>
    </>
  );
}

function Thread({ ticket, backTo, onChange }: { ticket: Ticket; backTo: { pathname: string; search: string }; onChange: (t: Ticket) => void }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => { bottom.current?.scrollIntoView({ block: "end" }); }, [ticket.messages.length]);

  const update = useAction((body: { status?: TicketStatus; priority?: TicketPriority }) =>
    api.patch<Ticket>(`/admin/tickets/${ticket.id}`, body));
  const reply = useAction((body: { message: string; status?: TicketStatus }) =>
    api.post<Ticket>(`/admin/tickets/${ticket.id}/messages`, body));

  const patch = async (body: { status?: TicketStatus; priority?: TicketPriority }) => {
    const t = await update.run(body);
    if (t) { onChange(t); toast("Ticket updated", "success"); }
  };

  const send = async (status?: TicketStatus) => {
    const message = text.trim();
    if (!message) return;
    const t = await reply.run({ message, status });
    if (t) { setText(""); onChange(t); toast(status ? `Replied and marked ${TICKET_STATUS[status].label.toLowerCase()}` : "Reply sent", "success"); }
  };

  return (
    <div className="flex h-full max-h-[70vh] flex-col">
      <div className="space-y-3 border-b border-line px-5 py-4">
        <div className="flex items-start gap-2">
          <Link to={backTo} className="mt-0.5 text-muted hover:text-fg lg:hidden" aria-label="Back to list"><Icon name="chevronLeft" className="size-5" /></Link>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold">{ticket.subject}</h2>
            <p className="mt-0.5 text-xs text-muted">
              #{ticket.id} from <Link to={`/admin/users/${ticket.user_id}`} className="text-fg hover:text-accent">{ticket.username}</Link>
              {" "}· opened {fmtDateTime(ticket.created_at)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 text-xs text-muted">
            Status
            <Select className="w-36" value={ticket.status} disabled={update.pending} onChange={(e) => patch({ status: e.target.value as TicketStatus })}>
              {STATUSES.map((s) => <option key={s} value={s}>{TICKET_STATUS[s].label}</option>)}
            </Select>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted">
            Priority
            <Select className="w-32" value={ticket.priority} disabled={update.pending} onChange={(e) => patch({ priority: e.target.value as TicketPriority })}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p[0]!.toUpperCase() + p.slice(1)}</option>)}
            </Select>
          </label>
        </div>
        <ErrorText>{update.error}</ErrorText>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {ticket.messages.map((m) => (
          <div key={m.id} className={cn("flex", m.is_admin && "justify-end")}>
            <div className={cn("max-w-[85%] rounded-xl border px-3.5 py-2.5", m.is_admin ? "border-accent/25 bg-accent-soft" : "border-line bg-raised")}>
              <div className="mb-1 flex items-center gap-2 text-[11px] text-muted">
                <span className="font-medium text-fg">{m.username}</span>
                {m.is_admin && <Badge tone="accent">Staff</Badge>}
                <span title={fmtDateTime(m.created_at)}>{fmtRelative(m.created_at)}</span>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.message}</p>
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>

      <div className="space-y-3 border-t border-line px-5 py-4">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" maxLength={5000} className="min-h-20" />
        <ErrorText>{reply.error}</ErrorText>
        <div className="flex flex-wrap justify-end gap-2">
          <Button disabled={!text.trim()} loading={reply.pending} onClick={() => send("resolved")}>Reply &amp; resolve</Button>
          <Button disabled={!text.trim()} loading={reply.pending} onClick={() => send("closed")}>Reply &amp; close</Button>
          <Button variant="primary" icon="send" disabled={!text.trim()} loading={reply.pending} onClick={() => send()}>Reply</Button>
        </div>
      </div>
    </div>
  );
}
